import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { map } from 'rxjs';
import { Registro, NOMBRE_MAX, TELEFONO_MAX } from './registro.model';
import { RegistrosService } from './registros.service';

/** Rechaza valores formados solo por espacios (la API exige contenido). */
export function sinSoloEspacios(control: AbstractControl): ValidationErrors | null {
  const valor = control.value;
  return typeof valor === 'string' && valor.length > 0 && valor.trim().length === 0 ? { vacio: true } : null;
}

/** Minúsculas y sin tildes, para buscar sin distinguir «José» de «jose». */
export function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Enlace tel: solo con dígitos, + * #; null si no queda un número marcable. */
export function enlaceTelefono(telefono: string): string | null {
  const marcable = telefono.replace(/[^\d+*#]/g, '');
  return /\d/.test(marcable) ? `tel:${marcable}` : null;
}

export interface Resultado {
  tipo: 'ok' | 'error';
  texto: string;
  /** Si nace en el listado (borrado), se repite visualmente junto a él: en móvil el formulario queda fuera de vista. */
  enLista?: boolean;
}

/** Transición del formulario que descartaría los cambios sin guardar. */
export type AccionDescarte =
  | { tipo: 'nuevo' }
  | { tipo: 'editar'; registro: Registro }
  | { tipo: 'cancelar' };

interface ValoresForm {
  nombre: string;
  telefono: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  private readonly api = inject(RegistrosService);
  private readonly fb = inject(FormBuilder);

  readonly nombreMax = NOMBRE_MAX;
  readonly telefonoMax = TELEFONO_MAX;

  readonly registros = signal<Registro[]>([]);
  readonly cargando = signal(true);
  readonly errorCarga = signal<string | null>(null);
  readonly editando = signal<Registro | null>(null);
  readonly guardando = signal(false);
  readonly erroresServidor = signal<Record<string, string>>({});
  readonly confirmandoId = signal<number | null>(null);
  readonly borrandoId = signal<number | null>(null);
  readonly errorBorrado = signal<string | null>(null);
  /** Resultado de la última acción (guardar/eliminar), junto al formulario. */
  readonly resultado = signal<Resultado | null>(null);
  /** Texto del buscador del listado (nombre o teléfono). */
  readonly busqueda = signal('');
  /**
   * Los errores de validación solo se muestran tras intentar guardar o tras
   * modificar el campo: pasar por un campo vacío no debe desplazar la página.
   */
  readonly intentado = signal(false);
  /** Transición a la espera de que el usuario decida si descarta los cambios sin guardar. */
  readonly pendiente = signal<AccionDescarte | null>(null);

  /** Listado en orden alfabético estable (empates por id). */
  readonly ordenados = computed(() =>
    [...this.registros()].sort(
      (a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }) || a.id - b.id,
    ),
  );

  /** Contactos que coinciden con la búsqueda (todos si está vacía). */
  readonly visibles = computed(() => {
    const q = normalizar(this.busqueda().trim());
    const todos = this.ordenados();
    if (!q) {
      return todos;
    }
    const soloNumero = /^[\d\s+\-().]+$/.test(q);
    const digitos = q.replace(/\D/g, '');
    return todos.filter(
      (r) =>
        normalizar(r.nombre).includes(q) ||
        normalizar(r.telefono).includes(q) ||
        (soloNumero && digitos.length > 0 && r.telefono.replace(/\D/g, '').includes(digitos)),
    );
  });

  readonly filtrando = computed(() => this.busqueda().trim().length > 0);

  private activadorBorrado: HTMLElement | null = null;

  readonly form = this.fb.nonNullable.group({
    nombre: ['', [Validators.required, Validators.maxLength(NOMBRE_MAX), sinSoloEspacios]],
    telefono: ['', [Validators.required, Validators.maxLength(TELEFONO_MAX), sinSoloEspacios]],
  });

  /** Valores con los que arrancó el formulario: vacíos al crear, los del contacto al editar. */
  private readonly base = signal<ValoresForm>({ nombre: '', telefono: '' });
  private readonly valores = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue())), {
    initialValue: this.form.getRawValue(),
  });
  /** ¿Difiere lo escrito del estado inicial? Volver a los valores iniciales lo deja limpio. */
  readonly sucio = computed(() => {
    const v = this.valores();
    const b = this.base();
    return v.nombre !== b.nombre || v.telefono !== b.telefono;
  });

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    this.cargando.set(true);
    this.errorCarga.set(null);
    this.api.listar().subscribe({
      next: (lista) => {
        this.registros.set(lista);
        this.cargando.set(false);
      },
      error: (e) => {
        this.errorCarga.set(this.mensaje(e, 'No se pudo cargar la lista de contactos.'));
        this.cargando.set(false);
      },
    });
  }

  /** ¿Se debe mostrar el error de validación de este campo? */
  mostrar(campo: 'nombre' | 'telefono'): boolean {
    const c = this.form.controls[campo];
    return c.invalid && (this.intentado() || c.dirty);
  }

  telefonoEnlace(registro: Registro): string | null {
    return enlaceTelefono(registro.telefono);
  }

  actualizarBusqueda(valor: string): void {
    this.busqueda.set(valor);
  }

  limpiarBusqueda(): void {
    this.busqueda.set('');
    this.enfocar('busqueda');
  }

  /** Atajo del listado (móvil): lleva al formulario vacío de alta. */
  nuevoContacto(): void {
    if (this.protegerCambios({ tipo: 'nuevo' })) {
      return;
    }
    this.ejecutar({ tipo: 'nuevo' });
  }

  editar(registro: Registro): void {
    if (this.protegerCambios({ tipo: 'editar', registro })) {
      return;
    }
    this.ejecutar({ tipo: 'editar', registro });
  }

  cancelarEdicion(): void {
    if (this.protegerCambios({ tipo: 'cancelar' })) {
      return;
    }
    this.ejecutar({ tipo: 'cancelar' });
  }

  /** Si hay cambios sin guardar, pide decisión en vez de ejecutar la transición. */
  private protegerCambios(accion: AccionDescarte): boolean {
    if (!this.sucio() || this.guardando() || this.pendiente()) {
      return false;
    }
    this.pendiente.set(accion);
    this.enfocar('seguir-editando');
    return true;
  }

  /** Mantiene el formulario tal cual: ni valores ni contexto de edición cambian. */
  seguirEditando(): void {
    if (!this.pendiente()) {
      return;
    }
    this.pendiente.set(null);
    this.enfocar('nombre');
  }

  /** Ejecuta solo la transición que el usuario había solicitado. */
  descartarCambios(): void {
    const accion = this.pendiente();
    if (!accion) {
      return;
    }
    this.pendiente.set(null);
    this.ejecutar(accion);
  }

  private ejecutar(accion: AccionDescarte): void {
    switch (accion.tipo) {
      case 'nuevo':
        this.editando.set(null);
        this.resultado.set(null);
        this.vaciarFormulario();
        this.enfocar('nombre');
        break;
      case 'editar':
        this.editando.set(accion.registro);
        this.confirmandoId.set(null);
        this.resultado.set(null);
        this.intentado.set(false);
        this.erroresServidor.set({});
        this.base.set({ nombre: accion.registro.nombre, telefono: accion.registro.telefono });
        this.form.reset({ nombre: accion.registro.nombre, telefono: accion.registro.telefono });
        this.enfocar('nombre');
        break;
      case 'cancelar': {
        const previo = this.editando();
        this.editando.set(null);
        this.resultado.set(null);
        this.vaciarFormulario();
        if (previo) {
          this.enfocar('editar-' + previo.id, 'nombre');
        }
        break;
      }
    }
  }

  private vaciarFormulario(): void {
    this.intentado.set(false);
    this.erroresServidor.set({});
    this.base.set({ nombre: '', telefono: '' });
    this.form.reset({ nombre: '', telefono: '' });
  }

  guardar(): void {
    if (this.guardando()) {
      return;
    }
    if (this.form.invalid) {
      this.intentado.set(true);
      this.form.markAllAsTouched();
      this.resultado.set({ tipo: 'error', texto: 'No se guardó el contacto: revisa los campos marcados.' });
      return;
    }
    const valores = {
      nombre: this.form.controls.nombre.value.trim(),
      telefono: this.form.controls.telefono.value.trim(),
    };
    const actual = this.editando();
    this.guardando.set(true);
    this.erroresServidor.set({});
    this.resultado.set(null);

    const peticion = actual ? this.api.actualizar(actual.id, valores) : this.api.crear(valores);
    peticion.subscribe({
      next: (guardado) => {
        this.guardando.set(false);
        this.resultado.set({
          tipo: 'ok',
          texto: actual ? `Cambios guardados: ${guardado.nombre}.` : `Contacto añadido: ${guardado.nombre}.`,
        });
        this.editando.set(null);
        this.vaciarFormulario();
        if (actual) {
          this.registros.update((l) => l.map((r) => (r.id === guardado.id ? guardado : r)));
        } else {
          this.registros.update((l) => [...l, guardado]);
        }
        this.enfocar('nombre');
      },
      error: (e: HttpErrorResponse) => {
        this.guardando.set(false);
        this.erroresServidor.set(this.erroresDeCampo(e));
        this.resultado.set({ tipo: 'error', texto: this.mensaje(e, 'No se pudo guardar el contacto.') });
      },
    });
  }

  pedirBorrado(registro: Registro, activador?: HTMLElement | null): void {
    this.errorBorrado.set(null);
    this.resultado.set(null);
    this.activadorBorrado = activador ?? (document.getElementById('eliminar-' + registro.id) as HTMLElement | null);
    this.confirmandoId.set(registro.id);
    this.enfocar('cancelar-borrado');
  }

  cancelarBorrado(): void {
    if (this.borrandoId() !== null) {
      return;
    }
    const id = this.confirmandoId();
    this.confirmandoId.set(null);
    this.errorBorrado.set(null);
    const activador = this.activadorBorrado;
    this.activadorBorrado = null;
    if (activador && activador.isConnected) {
      setTimeout(() => activador.focus());
    } else if (id !== null) {
      this.enfocar('eliminar-' + id, 'titulo-lista');
    }
  }

  /** Cierra el diálogo de borrado con Escape y mantiene el tabulador dentro de él. */
  teclaDialogo(ev: KeyboardEvent, dialogo: HTMLElement): void {
    this.teclaModal(ev, dialogo, () => this.cancelarBorrado());
  }

  /** Escape en el aviso de cambios sin guardar equivale a «Seguir editando» (la opción segura). */
  teclaDescarte(ev: KeyboardEvent, dialogo: HTMLElement): void {
    this.teclaModal(ev, dialogo, () => this.seguirEditando());
  }

  private teclaModal(ev: KeyboardEvent, dialogo: HTMLElement, cerrar: () => void): void {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      cerrar();
      return;
    }
    if (ev.key !== 'Tab') {
      return;
    }
    const botones = Array.from(dialogo.querySelectorAll<HTMLElement>('button:not([disabled])'));
    if (botones.length === 0) {
      ev.preventDefault();
      return;
    }
    const primero = botones[0];
    const ultimo = botones[botones.length - 1];
    const activo = document.activeElement;
    if (ev.shiftKey && (activo === primero || !dialogo.contains(activo))) {
      ev.preventDefault();
      ultimo.focus();
    } else if (!ev.shiftKey && (activo === ultimo || !dialogo.contains(activo))) {
      ev.preventDefault();
      primero.focus();
    }
  }

  confirmarBorrado(registro: Registro): void {
    if (this.borrandoId() !== null) {
      return;
    }
    this.borrandoId.set(registro.id);
    this.errorBorrado.set(null);
    this.api.eliminar(registro.id).subscribe({
      next: () => {
        const lista = this.visibles();
        const idx = lista.findIndex((r) => r.id === registro.id);
        const vecino = lista[idx + 1] ?? lista[idx - 1] ?? null;
        this.borrandoId.set(null);
        this.confirmandoId.set(null);
        this.activadorBorrado = null;
        this.registros.update((l) => l.filter((r) => r.id !== registro.id));
        if (this.editando()?.id === registro.id) {
          this.editando.set(null);
          this.vaciarFormulario();
        }
        this.resultado.set({ tipo: 'ok', texto: `Contacto eliminado: ${registro.nombre}.`, enLista: true });
        if (vecino) {
          this.enfocar('editar-' + vecino.id, 'titulo-lista');
        } else {
          this.enfocar('titulo-lista');
        }
      },
      error: (e) => {
        this.borrandoId.set(null);
        this.errorBorrado.set(this.mensaje(e, 'No se pudo eliminar el contacto.'));
        this.enfocar('cancelar-borrado');
      },
    });
  }

  trackId(_: number, r: Registro): number {
    return r.id;
  }

  /** Enfoca el primer id existente tras el siguiente renderizado. */
  private enfocar(...ids: string[]): void {
    setTimeout(() => {
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el) {
          el.focus();
          break;
        }
      }
    });
  }

  private erroresDeCampo(e: HttpErrorResponse): Record<string, string> {
    const resultado: Record<string, string> = {};
    const errores = e.error?.errors;
    if (e.status === 422 && errores && typeof errores === 'object') {
      for (const campo of Object.keys(errores)) {
        resultado[campo] = Object.values(errores[campo] as Record<string, string>).join(' ');
      }
    }
    return resultado;
  }

  private mensaje(e: unknown, porDefecto: string): string {
    if (e instanceof HttpErrorResponse) {
      if (e.status === 0) {
        return `${porDefecto} No hay conexión con el servidor; tus datos siguen en el formulario.`;
      }
      if (e.status === 422) {
        return 'No se guardó el contacto: el servidor ha rechazado los datos. Revísalos.';
      }
      if (e.status === 404) {
        return `${porDefecto} El contacto ya no existe.`;
      }
      return `${porDefecto} (error ${e.status})`;
    }
    return porDefecto;
  }
}
